import { Check, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SelectChoice = { value: string; label: string; disabled?: boolean; note?: string };

type MenuPosition = { left: number; top: number; width: number; maxHeight: number; dropUp: boolean };

const ROW_HEIGHT = 46;
const MAX_MENU_HEIGHT = 320;

export function SelectMenu({
  value,
  onChange,
  choices,
  ariaLabel,
  className,
  id
}: {
  value: string;
  onChange: (value: string) => void;
  choices: SelectChoice[];
  ariaLabel?: string;
  className?: string;
  id?: string;
}) {
  const generatedId = useId();
  const listId = `${id ?? generatedId}-listbox`;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const selectedIndex = Math.max(0, choices.findIndex((choice) => choice.value === value));
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef({ term: '', timer: 0 });

  const selected = choices[selectedIndex] ?? choices[0];

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const desired = Math.min(MAX_MENU_HEIGHT, choices.length * ROW_HEIGHT + 10);
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const dropUp = below < desired && above > below;
    setPosition({
      left: rect.left,
      top: dropUp ? rect.top - 6 : rect.bottom + 6,
      width: rect.width,
      maxHeight: Math.max(120, Math.min(desired, dropUp ? above : below)),
      dropUp
    });
  }, [choices.length]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const handle = () => measure();
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  function firstEnabledIndex(start = 0, direction: 1 | -1 = 1) {
    if (!choices.length) return 0;
    let index = start;
    for (let step = 0; step < choices.length; step += 1) {
      if (!choices[index]?.disabled) return index;
      index = (index + direction + choices.length) % choices.length;
    }
    return selectedIndex;
  }

  function openMenu() {
    setActiveIndex(firstEnabledIndex(selectedIndex));
    setOpen(true);
  }

  function commit(index: number) {
    const choice = choices[index];
    if (!choice || choice.disabled) return;
    onChange(choice.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function move(direction: 1 | -1) {
    setActiveIndex((current) => {
      let next = current;
      for (let step = 0; step < choices.length; step += 1) {
        next = (next + direction + choices.length) % choices.length;
        if (!choices[next]?.disabled) return next;
      }
      return current;
    });
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openMenu();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(firstEnabledIndex(0, 1));
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(firstEnabledIndex(choices.length - 1, -1));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      commit(activeIndex);
    } else if (event.key === 'Tab') {
      setOpen(false);
    } else if (event.key.length === 1) {
      window.clearTimeout(typeahead.current.timer);
      typeahead.current.term += event.key.toLowerCase();
      typeahead.current.timer = window.setTimeout(() => { typeahead.current.term = ''; }, 600);
      const match = choices.findIndex((choice) => !choice.disabled && choice.label.toLowerCase().startsWith(typeahead.current.term));
      if (match >= 0) setActiveIndex(match);
    }
  }

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        id={id}
        className={`select-menu-trigger ${open ? 'open' : ''} ${className ?? ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span>{selected?.label ?? ''}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {open && position && createPortal(
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={ariaLabel}
          className={`select-menu-list ${position.dropUp ? 'drop-up' : ''}`}
          style={{
            left: position.left,
            top: position.top,
            width: position.width,
            maxHeight: position.maxHeight
          }}
          onKeyDown={onKeyDown}
        >
          {choices.map((choice, index) => (
            <div
              key={choice.value}
              id={`${listId}-${index}`}
              data-index={index}
              role="option"
              aria-selected={choice.value === value}
              aria-disabled={choice.disabled || undefined}
              className={`select-menu-option ${index === activeIndex ? 'active' : ''} ${choice.value === value ? 'selected' : ''} ${choice.disabled ? 'disabled' : ''}`}
              onMouseEnter={() => !choice.disabled && setActiveIndex(index)}
              onClick={() => commit(index)}
            >
              <span><b>{choice.label}</b>{choice.note && <small>{choice.note}</small>}</span>
              {choice.value === value && <Check size={15} aria-hidden="true" />}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
